/** As the subrentals API sends it (module `subrentals`; the web app does not import server modules). */
export const RENTALS_RESOURCE = 'subrentals.rentals';
export const PRICES_RESOURCE = 'subrentals.prices';
export const SUBRENTALS_KEY = ['subrentals'] as const;
export const rentalsKey = (eventId: string) => [...SUBRENTALS_KEY, 'event', eventId] as const;

export type RentalStatus = 'planowane' | 'potwierdzone' | 'odebrane' | 'zwrocone' | 'anulowane';
export const RENTAL_STATUSES: Record<RentalStatus, string> = { planowane: 'Planowane', potwierdzone: 'Potwierdzone', odebrane: 'Odebrane', zwrocone: 'Zwrócone', anulowane: 'Anulowane' };
/** Owner 2026-11-01: what a person picks — „Anulowane” is set by the program with the event, never by hand. */
export const MANUAL_STATUSES: readonly RentalStatus[] = ['planowane', 'potwierdzone', 'odebrane', 'zwrocone'];
/**
 * Owner 2026-11-01: a rental of a closed event. `anulowane`: cancelled with it — struck through, greyed, a red tag
 * (`label`: „Anulowano – wydarzenie odwołane”); `sprawdz`: picked up or returned before — it counts, „… – sprawdź
 * rozliczenie z dostawcą”.
 */
export type RentalMark = { kind: 'anulowane' | 'sprawdz'; reason: 'odwolane' | 'rezygnacja' | 'usuniete'; label: string };
export type CostMode = 'cennik' | 'reczny';

/** Fields with permissions are absent for a role that may not see them (the cost and price are sensitive). */
export type RentalView = {
  id: string;
  eventId: string;
  /** Null: a supplier off our list (`offList`, owner 2026-10-24 16:15). */
  supplierId?: string | null;
  supplierName?: string;
  /** Owner 2026-10-24 16:15 (pkt 4): „Inny (spoza listy)” — the supplier's typed name and contact, the gear's description. */
  offList?: boolean;
  offSupplierName?: string;
  contactPhone?: string;
  contactEmail?: string;
  contactPerson?: string;
  description?: string;
  /** Off-list: what is still to fill in („do uzupełnienia”), as the role may see it. */
  toFill?: Array<'dostawca' | 'kontakt' | 'opis'>;
  /** Null: a position only the supplier has, or a cost only ("Sam koszt u dostawcy"). */
  itemId?: string | null;
  itemName?: string;
  name?: string;
  /** The supplier's position the cost was taken from (owner 2026-10-23 21:23); hidden with the supplier. */
  priceId?: string | null;
  costOnly?: boolean;
  /** Kept by the quote: quantity, supplier, item and cost are changed there (review N1/N2). */
  keptByQuote?: boolean;
  /** Kept by the quote but no longer in its governing version (picked up or returned). */
  outsideQuote?: boolean;
  /** Review I-new-1: the quote controls it now (Planowane/Potwierdzone, in its governing version) — only then blocked here. */
  quoteControlled?: boolean;
  quantity?: number;
  firstDay?: string;
  lastDay?: string;
  days?: number;
  costMode?: CostMode;
  priceDayGrosze?: number | null;
  costGrosze?: number;
  status?: RentalStatus;
  /** Owner 2026-11-01: the status before „Anulowane” (hidden with the status). */
  cancelledFrom?: RentalStatus | null;
  /** Owner 2026-11-01: the mark of a closed event's rental (hidden with the status). */
  mark?: RentalMark | null;
  /** Whether it counts in sums (false: „Anulowane”); hidden with the cost. */
  counted?: boolean;
  returnPersonId?: string | null;
  returnPersonName?: string | null;
  notes?: string;
  version: number;
};

export type PriceView = {
  id: string;
  supplierId?: string;
  supplierName?: string;
  itemId?: string | null;
  itemName?: string | null;
  /** The category of our item (the supplier's catalog is grouped like ours); null for a position only the supplier has. */
  categoryId?: string | null;
  name?: string;
  priceDayGrosze?: number;
  /** "Cena dla klienta" per day (owner 2026-10-22): the quote line's price; null = our usual price. Sensitive. */
  clientPriceDayGrosze?: number | null;
  /** "Ilość posiadanych" at the supplier; null = not known. */
  quantity?: number | null;
  description?: string;
  notes?: string;
  version: number;
};

/**
 * A position of a supplier's catalog offered in „Dopożycz” (`GET /api/subrentals/match`, owner 2026-10-23 21:23):
 * `exact` is linked to our item, `similar` only has a matching name. Fields as the role may see them.
 */
export type MatchCandidate = {
  priceId: string;
  supplierId?: string;
  supplierName?: string;
  itemId?: string | null;
  itemName?: string | null;
  name?: string;
  priceDayGrosze?: number;
  quantity?: number | null;
  /** `other_item`: linked to ANOTHER item of ours — shown, never chosen (review C1). */
  match: 'exact' | 'similar' | 'other_item';
  /** False: the supplier has a position of this item already, so this one cannot be linked to it. */
  linkable?: boolean;
};
export type MatchResult = { candidates: MatchCandidate[]; pickedPriceId: string | null };

/** A supplier to choose in "Katalogi dostawców" (`GET /api/subrentals/suppliers`). */
export type SupplierSummary = { id: string; name: string; positions: number };

/** What "Dopożycz" at a shortage of the packlist fills in. */
export type RentalPreset = {
  itemId: string;
  itemName: string;
  quantity: number;
  firstDay: string;
  lastDay: string;
  /** Typed in the form before „Wskaż w katalogu” (review I2): put back on return. */
  carried?: { supplierId?: string; notes?: string; status?: string; returnPersonId?: string; costMode?: CostMode; cost?: string };
};

/** "6–9 lis" style day range for the list. */
const DAY = new Intl.DateTimeFormat('pl-PL', { timeZone: 'UTC', day: 'numeric', month: 'short' });
export function formatDays(first?: string, last?: string): string {
  if (!first || !last) return '';
  const f = DAY.format(new Date(`${first}T00:00:00Z`));
  return first === last ? f : `${f} – ${DAY.format(new Date(`${last}T00:00:00Z`))}`;
}

/** The day `n` days from `day` ('YYYY-MM-DD'). */
export function shiftDay(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function presetOf(
  line: { itemId: string; itemName: string; firstDay?: string; lastDay?: string; followsEvent?: boolean; extraHeld?: string[] | undefined; availability: { missing: number } },
  /** Owner 06:47: the event's "Rezerwuj sprzęt także" days; a line on the whole event reserves them too. */
  event: { reserveDaysBefore?: number | undefined; reserveDaysAfter?: number | undefined } = {},
): RentalPreset {
  const before = line.followsEvent ? (event.reserveDaysBefore ?? 0) : 0;
  const after = line.followsEvent ? (event.reserveDaysAfter ?? 0) : 0;
  const first = line.firstDay ? shiftDay(line.firstDay, -before) : '';
  const last = line.lastDay ? shiftDay(line.lastDay, after) : '';
  // review 5 (owner 2026-10-24 15:29): the item's „Dni rezerwacji…” are reserved too — the rental covers them
  const held = line.extraHeld ?? [];
  return {
    itemId: line.itemId,
    itemName: line.itemName,
    quantity: line.availability.missing,
    firstDay: first && held.length > 0 ? [first, ...held].sort()[0]! : first,
    lastDay: last && held.length > 0 ? [last, ...held].sort().at(-1)! : last,
  };
}
